/**
 * iProov Web SDK Utilities
 *
 * Provides utility functions for checking iProov Web SDK availability.
 * The @iproov/web-sdk package provides the <iproov-me> custom element.
 *
 * Implementation uses declarative web component approach as recommended:
 * https://github.com/iProov/web#frontend
 *
 * Flow:
 * 1. Backend provides token + baseUrl from POST /v1/verifications/faces
 * 2. Frontend renders <iproov-me> web component with token and base_url attributes
 * 3. iProov web component communicates directly with iProov servers
 * 4. Frontend listens to iProov events and polls backend for final verification result
 * 5. Backend validates with iProov and provides normalized result
 */

/**
 * Check if iProov Web SDK is loaded
 */
export function isIProovLoaded(): boolean {
  return typeof window !== "undefined" && customElements.get("iproov-me") !== undefined;
}

let webSdkPromise: Promise<unknown> | null = null;

/** The `@iproov/web-sdk` chunk failed to load, as opposed to a session-creation failure. */
export class IProovSdkLoadError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "IProovSdkLoadError";
  }
}

const SDK_RELOAD_KEY = "idme:iproov-sdk-reload";

/** Browsers cache a failed dynamic import, so the only retry is a fresh document (VEX-8077). Once per tab. */
export function reloadOnceForIProovSdk(): boolean {
  try {
    if (window.sessionStorage.getItem(SDK_RELOAD_KEY)) return false;
    window.sessionStorage.setItem(SDK_RELOAD_KEY, "1");
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

export function clearIProovSdkReload(): void {
  try {
    window.sessionStorage.removeItem(SDK_RELOAD_KEY);
  } catch {
    // Storage unavailable: nothing was set.
  }
}

/** Registers <iproov-me>. An element rendered before this resolves is upgraded in place. */
export function loadIProovWebSdk(): Promise<unknown> {
  if (!webSdkPromise) {
    webSdkPromise = import("@iproov/web-sdk").catch((err) => {
      webSdkPromise = null;
      throw err;
    });
  }
  return webSdkPromise;
}

/**
 * Wait for iProov Web SDK to load
 *
 * @param timeout - Maximum time to wait in milliseconds
 * @returns Promise that resolves when iProov is loaded
 */
export function waitForIProov(timeout: number = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isIProovLoaded()) {
      resolve();
      return;
    }

    const startTime = Date.now();
    const checkInterval = setInterval(() => {
      if (isIProovLoaded()) {
        clearInterval(checkInterval);
        resolve();
      } else if (Date.now() - startTime > timeout) {
        clearInterval(checkInterval);
        reject(new Error("iProov Web SDK failed to load within timeout"));
      }
    }, 100);
  });
}
